import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Clock, Loader2, Send } from "lucide-react";

export const daysSince = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};

export const ActivationBadge = ({ c, onResent }) => {
  const [sending, setSending] = useState(false);
  if (!c.needs_activation) return null;
  const days = daysSince(c.last_invite_at || c.created_at);
  const stale = days !== null && days >= 3;

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/staff/cases/${c.case_id}/resend-invite`);
      toast.success(data.invite_sent ? `Invite re-sent to ${data.email}` : "New invite link created (email not configured)");
      if (!data.invite_sent) navigator.clipboard?.writeText(data.invite_link);
      onResent?.(c.case_id, data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend invite");
    } finally { setSending(false); }
  };

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`activation-${c.case_id}`}>
      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${stale ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#FEF3C7] text-[#92600A]"}`}
        data-testid={`pending-activation-${c.case_id}`}>
        <Clock className="h-3 w-3" /> Pending activation{days !== null ? ` · ${days}d` : ""}
        {c.invite_count > 1 ? ` · ${c.invite_count} invites` : ""}
      </span>
      <button onClick={resend} disabled={sending} data-testid={`resend-invite-${c.case_id}`}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#114A31] hover:text-[#1A6B46] underline-offset-2 hover:underline disabled:opacity-50">
        {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Resend invite
      </button>
    </span>
  );
};

export const PipelineFilter = ({ value, onChange, counts }) => {
  const opts = [
    { key: "all", label: "All" },
    { key: "activation", label: "Needs activation" },
    { key: "onboarding", label: "Onboarding" },
    { key: "vault", label: "Vault (free)" },
    { key: "active", label: "Active" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="pipeline-filter">
      {opts.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)} data-testid={`pipeline-filter-${o.key}`}
          className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
            value === o.key ? "bg-[#114A31] text-white border-[#114A31]" : "bg-white text-[#595D5A] border-[#E5E5E5] hover:border-[#114A31] hover:text-[#114A31]"}`}>
          {o.label}{counts?.[o.key] !== undefined ? ` (${counts[o.key]})` : ""}
        </button>
      ))}
    </div>
  );
};

export const applyPipelineFilter = (cases, f) => {
  if (f === "activation") return cases.filter((c) => c.needs_activation);
  if (f === "onboarding") return cases.filter((c) => c.status === "Onboarding");
  if (f === "vault") return cases.filter((c) => c.status === "Vault");
  if (f === "active") return cases.filter((c) => !["Complete", "Onboarding", "Vault"].includes(c.status));
  return cases;
};

export const pipelineCounts = (cases) => ({
  all: cases.length,
  activation: cases.filter((c) => c.needs_activation).length,
  onboarding: cases.filter((c) => c.status === "Onboarding").length,
  vault: cases.filter((c) => c.status === "Vault").length,
  active: cases.filter((c) => !["Complete", "Onboarding", "Vault"].includes(c.status)).length,
});
